import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from "react";
import { Link, useParams } from "react-router-dom";
// dnd-kit and external icon imports
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowLeft,
  Clock,
  GripVertical,
  Info,
  ListOrdered,
  MoreVertical,
  Plus,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, SectionHeader } from "@/components/ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
// internal imports
import { VISAVALUE } from "../../../types/visa.type";
import {
  DefaultTemplate,
  DefaultTemplateStatus,
  DefaultTemplateVersion,
  TemplateSection
} from "../../../api/types/default-template";
import { DraftType, DRAFT_TYPE_LABELS } from "../../../api/types/draft";
import { DefaultTemplatesAPI } from "../../../api/default-templates";
import { SortableSection } from "../../../components/common/ManageSectionsModal";
import { AutoSaveIndicator } from "../../../components/common/AutoSaveIndicator";
import SectionContainer from "./SectionContainer";
import { ProseTextArea } from "../../../components/common/ProseTextArea";
import { useAutoSaveContext } from "../../../contexts/AutoSaveContext";
import {
  TemplateVersionHistoryDrawer,
  TemplateVersionLike
} from "../../../features/templateVersioning/components/TemplateVersionHistoryDrawer";

type EditableSection = TemplateSection & { section_content?: string };

type SavedSnapshot = {
  visa: VISAVALUE | "";
  visa_subtype: string;
  draft_type: DraftType | "";
  is_standard: boolean;
  sections: EditableSection[];
};

// Sidebar item component (top-level to satisfy react/no-unstable-nested-components)
type SidebarSortableItemProps = {
  id: string;
  title: string;
  isSelected: boolean;
  onClick: () => void;
};

const SidebarSortableItem: React.FC<SidebarSortableItemProps> = ({
  id,
  title,
  isSelected,
  onClick
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  } as React.CSSProperties;
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex h-8 cursor-pointer items-center gap-2 rounded-md px-2 transition-colors duration-100",
        isSelected
          ? "bg-fill-3 font-medium text-fg"
          : "text-fg-2 hover:bg-fill-1 hover:text-fg"
      )}
      onClick={onClick}
    >
      <span className="min-w-0 flex-1 truncate">{title}</span>
      <span
        {...attributes}
        {...listeners}
        className="flex cursor-grab text-fg-3 hover:text-fg"
      >
        <GripVertical className="size-3.5" />
      </span>
    </div>
  );
};

const InfoHint = ({ label, content }: { label: string; content: string }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        aria-label={label}
        className="flex rounded-xs text-fg-3 outline-none hover:text-fg focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        <Info className="size-3.5" />
      </button>
    </TooltipTrigger>
    <TooltipContent side="top">{content}</TooltipContent>
  </Tooltip>
);

const RequiredMark = () => (
  <span className="text-danger" aria-hidden>
    *
  </span>
);

export const EditTemplate: React.FC = () => {
  const { templateId } = useParams();
  const [visa, setVisa] = useState<VISAVALUE | "">("");
  const [visaSubtype, setVisaSubtype] = useState<string>("");
  const [draftType, setDraftType] = useState<DraftType | "">("");
  const [isStandard, setIsStandard] = useState<boolean>(false);
  const [status, setStatus] = useState<DefaultTemplateStatus | "">("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sections, setSections] = useState<EditableSection[]>([]);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [highlightedSection, setHighlightedSection] = useState<string | null>(
    null
  );
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [lastUpdatedByEmail, setLastUpdatedByEmail] = useState<string | null>(
    null
  );
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [isOpen, setIsOpen] = useState(false);
  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);
  const [sectionToDelete, setSectionToDelete] =
    useState<EditableSection | null>(null);
  const savedSnapshotRef = useRef<SavedSnapshot | null>(null);
  const { startAutoSave, flushAutoSave, autoSaveStatus } = useAutoSaveContext();
  const latestSnapshotRef = useRef({
    visa: "" as VISAVALUE | "",
    visaSubtype: "",
    draftType: "" as DraftType | "",
    isStandard: false,
    sections: [] as EditableSection[]
  });

  const publishedId = useId();
  const visaId = useId();
  const visaSubtypeId = useId();
  const draftTypeId = useId();
  const standardId = useId();

  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [templateVersions, setTemplateVersions] = useState<
    DefaultTemplateVersion[]
  >([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [isLoadingMoreVersions, setIsLoadingMoreVersions] = useState(false);
  const [hasMoreVersions, setHasMoreVersions] = useState(false);
  const [versioningError, setVersioningError] = useState<string | null>(null);
  const [restoringVersionId, setRestoringVersionId] = useState<string | null>(
    null
  );
  const [pendingRestoreVersion, setPendingRestoreVersion] =
    useState<TemplateVersionLike | null>(null);
  const [isRestoreConfirmOpen, setIsRestoreConfirmOpen] = useState(false);

  const versionsCountRef = useRef(0);
  const isLoadingVersionsRef = useRef(false);
  const isLoadingMoreVersionsRef = useRef(false);

  useEffect(() => {
    versionsCountRef.current = templateVersions.length;
  }, [templateVersions.length]);

  const VERSIONS_PAGE_SIZE = 10;

  const normalizeVersions = useCallback(
    (items: DefaultTemplateVersion[]): DefaultTemplateVersion[] => {
      return items
        .map((item, idx) => {
          const record = item as unknown as Record<string, unknown>;
          const rawVersionId =
            typeof record.version_id === "string"
              ? record.version_id
              : typeof record.id === "string"
              ? record.id
              : null;
          const rawTemplateId =
            typeof record.template_id === "string"
              ? record.template_id
              : typeof record.default_template_id === "string"
              ? record.default_template_id
              : templateId;
          const rawCreatedAt =
            typeof record.created_at === "string" ? record.created_at : "";

          const version_id =
            rawVersionId ?? `${rawTemplateId ?? "template"}:${idx}`;
          const template_id = rawTemplateId ?? templateId ?? "";
          const created_at = rawCreatedAt || new Date().toISOString();

          return {
            ...record,
            version_id,
            template_id,
            created_at
          } as DefaultTemplateVersion;
        })
        .filter((v) => Boolean(v.version_id));
    },
    [templateId]
  );

  useEffect(() => {
    latestSnapshotRef.current = {
      visa,
      visaSubtype,
      draftType,
      isStandard,
      sections
    };
  }, [visa, visaSubtype, draftType, isStandard, sections]);

  const fetchTemplateVersions = useCallback(
    async (opts?: { append?: boolean }) => {
      if (!templateId) return;
      const append = Boolean(opts?.append);

      if (append) {
        if (isLoadingMoreVersionsRef.current || isLoadingVersionsRef.current)
          return;
        isLoadingMoreVersionsRef.current = true;
        setIsLoadingMoreVersions(true);
      } else {
        isLoadingVersionsRef.current = true;
        setIsLoadingVersions(true);
        setVersioningError(null);
        setHasMoreVersions(false);
      }

      try {
        const offset = append ? versionsCountRef.current : 0;
        const raw = await DefaultTemplatesAPI.listVersions(templateId, {
          limit: VERSIONS_PAGE_SIZE,
          offset
        });
        const normalized = normalizeVersions(raw);

        setHasMoreVersions(raw.length === VERSIONS_PAGE_SIZE);
        setTemplateVersions((prev) => {
          if (!append) return normalized;
          const seen = new Set(prev.map((v) => v.version_id));
          const next = [...prev];
          normalized.forEach((v) => {
            if (!seen.has(v.version_id)) {
              seen.add(v.version_id);
              next.push(v);
            }
          });
          return next;
        });
      } catch (e) {
        if (!append) {
          setVersioningError("Failed to load version history.");
        } else {
          toast.error("Couldn't load more versions", {
            description: "Please try again.",
            duration: 4000
          });
        }
      } finally {
        if (append) {
          isLoadingMoreVersionsRef.current = false;
          setIsLoadingMoreVersions(false);
        } else {
          isLoadingVersionsRef.current = false;
          setIsLoadingVersions(false);
        }
      }
    },
    [normalizeVersions, templateId]
  );

  useEffect(() => {
    if (!isVersionHistoryOpen) return;
    fetchTemplateVersions().catch(() => {});
  }, [isVersionHistoryOpen, fetchTemplateVersions]);

  const handleRestoreClick = useCallback((v: TemplateVersionLike) => {
    setPendingRestoreVersion(v);
    setIsRestoreConfirmOpen(true);
  }, []);

  const applyRestoredTemplateToState = useCallback((tpl: DefaultTemplate) => {
    setVisa(tpl.visa);
    setVisaSubtype(tpl.visa_subtype || "");
    setDraftType(tpl.draft_type);
    setIsStandard(Boolean(tpl.is_standard));
    setStatus(tpl.status);
    setSections(tpl.sections || []);

    const ids = (tpl.sections || [])
      .map((s) => s.section_name)
      .filter(Boolean) as string[];
    setExpandedSections(ids);
    setHighlightedSection(ids.length > 0 ? ids[0] : null);

    const mostRecentAt = tpl.updated_at || tpl.created_at || null;
    const mostRecentEmail =
      tpl.updated_by_email ||
      tpl.created_by_email ||
      tpl.updated_by ||
      tpl.created_by ||
      null;
    setLastUpdatedAt(
      mostRecentAt
        ? new Date(mostRecentAt).toString() !== "Invalid Date"
          ? new Date(mostRecentAt).toLocaleString(undefined, {
              timeZoneName: "short"
            })
          : mostRecentAt
        : null
    );
    setLastUpdatedByEmail(mostRecentEmail);

    latestSnapshotRef.current = {
      visa: tpl.visa,
      visaSubtype: tpl.visa_subtype || "",
      draftType: tpl.draft_type,
      isStandard: Boolean(tpl.is_standard),
      sections: tpl.sections || []
    };
    savedSnapshotRef.current = {
      visa: tpl.visa,
      visa_subtype: tpl.visa_subtype || "",
      draft_type: tpl.draft_type,
      is_standard: Boolean(tpl.is_standard),
      sections: tpl.sections || []
    };
  }, []);

  const performRestore = useCallback(async () => {
    if (!templateId || !pendingRestoreVersion?.version_id) return;

    const versionId = pendingRestoreVersion.version_id;
    setRestoringVersionId(versionId);
    try {
      await flushAutoSave();
      const restored = await DefaultTemplatesAPI.restoreVersion(
        templateId,
        versionId
      );
      applyRestoredTemplateToState(restored);

      toast.success("Version restored", {
        description: "This template has been restored to the selected version.",
        duration: 4000
      });

      setIsRestoreConfirmOpen(false);
      setPendingRestoreVersion(null);

      await fetchTemplateVersions();
      setIsVersionHistoryOpen(false);
    } catch (e) {
      toast.error("Restore failed", {
        description: "We couldn't restore that version. Please try again.",
        duration: 5000
      });
    } finally {
      setRestoringVersionId(null);
    }
  }, [
    applyRestoredTemplateToState,
    fetchTemplateVersions,
    flushAutoSave,
    pendingRestoreVersion,
    templateId
  ]);

  const persistLatestTemplate = useCallback(async (): Promise<void> => {
    if (!templateId) return;
    const snapshot = latestSnapshotRef.current;
    if (!snapshot.visa || !snapshot.draftType) return;

    const updated = await DefaultTemplatesAPI.update(templateId, {
      visa: snapshot.visa as VISAVALUE,
      visa_subtype: snapshot.visaSubtype || undefined,
      draft_type: snapshot.draftType as DraftType,
      is_standard: snapshot.isStandard,
      sections: snapshot.sections
    });

    const mostRecentAt = updated.updated_at || updated.created_at || null;
    const mostRecentEmail =
      updated.updated_by_email ||
      updated.created_by_email ||
      updated.updated_by ||
      updated.created_by ||
      null;
    setLastUpdatedAt(
      mostRecentAt
        ? new Date(mostRecentAt).toString() !== "Invalid Date"
          ? new Date(mostRecentAt).toLocaleString(undefined, {
              timeZoneName: "short"
            })
          : mostRecentAt
        : null
    );
    setLastUpdatedByEmail(mostRecentEmail);

    savedSnapshotRef.current = {
      visa: snapshot.visa,
      visa_subtype: snapshot.visaSubtype || "",
      draft_type: snapshot.draftType,
      is_standard: snapshot.isStandard,
      sections: snapshot.sections
    };
  }, [templateId]);

  useEffect(() => {
    const run = async () => {
      try {
        setIsLoading(true);
        const tpl = await DefaultTemplatesAPI.getById(templateId || "");
        setVisa(tpl.visa);
        setVisaSubtype(tpl.visa_subtype || "");
        setDraftType(tpl.draft_type);
        setIsStandard(tpl.is_standard);
        setStatus(tpl.status);
        setSections(tpl.sections || []);
        const ids = (tpl.sections || [])
          .map((s) => s.section_name)
          .filter(Boolean) as string[];
        setExpandedSections(ids);
        const mostRecentAt = tpl.updated_at || tpl.created_at || null;
        const mostRecentEmail =
          tpl.updated_by_email ||
          tpl.created_by_email ||
          tpl.updated_by ||
          tpl.created_by ||
          null;
        setLastUpdatedAt(
          mostRecentAt
            ? new Date(mostRecentAt).toString() !== "Invalid Date"
              ? new Date(mostRecentAt).toLocaleString(undefined, {
                  timeZoneName: "short"
                })
              : mostRecentAt
            : null
        );
        setLastUpdatedByEmail(mostRecentEmail);
        savedSnapshotRef.current = {
          visa: tpl.visa,
          visa_subtype: tpl.visa_subtype || "",
          draft_type: tpl.draft_type,
          is_standard: tpl.is_standard,
          sections: tpl.sections || []
        };
      } catch (error) {
        toast.error("Failed to load template");
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [templateId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await flushAutoSave();
      await persistLatestTemplate();
      toast.success("Template updated", { duration: 3000 });
    } catch (error) {
      toast.error("Update failed", {
        description:
          error instanceof Error ? error.message : "An error occurred",
        duration: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const canTogglePublish =
    status === DefaultTemplateStatus.READY ||
    status === DefaultTemplateStatus.PUBLISHED;

  const hasUnsavedChanges = useMemo(() => {
    if (!savedSnapshotRef.current) return false;
    const current = {
      visa,
      visa_subtype: visaSubtype || "",
      draft_type: draftType,
      is_standard: isStandard,
      sections
    };
    try {
      return (
        JSON.stringify(current) !== JSON.stringify(savedSnapshotRef.current)
      );
    } catch {
      return true;
    }
  }, [visa, visaSubtype, draftType, isStandard, sections]);

  const handleTogglePublish = async () => {
    if (!templateId || !canTogglePublish) return;
    if (autoSaveStatus === "saving") {
      toast.info("Please wait for autosave to finish", { duration: 2500 });
      return;
    }
    if (autoSaveStatus === "error") {
      toast.warning("Fix autosave errors before publishing", {
        duration: 3000
      });
      return;
    }
    if (status !== DefaultTemplateStatus.PUBLISHED && hasUnsavedChanges) {
      toast.warning("Save changes before publishing", { duration: 3000 });
      return;
    }
    setIsPublishing(true);
    try {
      const newStatus =
        status === DefaultTemplateStatus.PUBLISHED
          ? DefaultTemplateStatus.INACTIVE
          : DefaultTemplateStatus.PUBLISHED;
      const updated = await DefaultTemplatesAPI.updateStatus(
        templateId,
        newStatus
      );
      setStatus(updated.status);
      toast.success(
        newStatus === DefaultTemplateStatus.PUBLISHED
          ? "Template published"
          : "Template unpublished",
        { duration: 3000 }
      );
    } catch (error) {
      toast.error("Status update failed", {
        description:
          error instanceof Error ? error.message : "An error occurred",
        duration: 5000
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const toggleSectionFromHeader = (sectionName: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionName)
        ? prev.filter((i) => i !== sectionName)
        : [...prev, sectionName]
    );
  };

  const expandSectionFromSidebar = (sectionName: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionName) ? prev : [...prev, sectionName]
    );
    setHighlightedSection(sectionName);
    setTimeout(() => {
      sectionRefs.current[sectionName]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest"
      });
    }, 50);
  };

  const setSectionRef = useCallback(
    (sectionName: string) => (el: HTMLDivElement | null) => {
      sectionRefs.current[sectionName] = el;
    },
    []
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  );

  const handleSidebarDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSections((prev) => {
      const oldIndex = prev.findIndex((s) => s.section_name === active.id);
      const newIndex = prev.findIndex((s) => s.section_name === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      latestSnapshotRef.current = {
        ...latestSnapshotRef.current,
        sections: next
      };
      startAutoSave(persistLatestTemplate);
      return next;
    });
  };

  const handleUpdateSectionContent = (
    plainText: string,
    sectionName: string
  ) => {
    setSections((prev) => {
      const nextSections = prev.map((s) =>
        s.section_name === sectionName
          ? {
              ...s,
              section_content: plainText
            }
          : s
      );
      latestSnapshotRef.current = {
        ...latestSnapshotRef.current,
        sections: nextSections
      };
      return nextSections;
    });
    startAutoSave(persistLatestTemplate);
  };

  const handleSaveTitle = async (sectionName: string, value: string) => {
    setSections((prev) => {
      const next = prev.map((s) =>
        s.section_name === sectionName ? { ...s, section_name: value } : s
      );
      latestSnapshotRef.current = {
        ...latestSnapshotRef.current,
        sections: next
      };
      return next;
    });
    startAutoSave(persistLatestTemplate);
  };

  const handleSaveSortedSections = async (updated: SortableSection[]) => {
    const newSections: EditableSection[] = updated.map((u) => {
      const original = sections.find((s) => s.section_name === u.id);
      return original
        ? { ...original, section_name: u.title }
        : {
            id: String(u.id),
            section_name: u.title,
            section_content: ""
          };
    });
    setSections(newSections);
    setExpandedSections(
      newSections.map((s) => s.section_name).filter(Boolean) as string[]
    );
    latestSnapshotRef.current = {
      ...latestSnapshotRef.current,
      sections: newSections
    };
    startAutoSave(persistLatestTemplate);
  };

  const handleAddSection = () => {
    const newId = `section_${Date.now()}`;
    const newSection: EditableSection = {
      id: newId,
      section_name: `Section ${sections.length + 1}`,
      section_content: ""
    };
    setSections((prev) => {
      const next = [...prev, newSection];
      latestSnapshotRef.current = {
        ...latestSnapshotRef.current,
        sections: next
      };
      return next;
    });
    setExpandedSections((prev) => [...prev, newSection.section_name ?? ""]);
    startAutoSave(persistLatestTemplate);
  };

  const handleDeleteSection = (sectionName: string) => {
    setSections((prev) => {
      const next = prev.filter((s) => s.section_name !== sectionName);
      latestSnapshotRef.current = {
        ...latestSnapshotRef.current,
        sections: next
      };
      return next;
    });
    setExpandedSections((prev) => prev.filter((id) => id !== sectionName));
    startAutoSave(persistLatestTemplate);
  };

  const sectionIds = sections.map((s) => s.section_name ?? "");

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-y-auto bg-surface">
      <div className="mx-auto flex w-full max-w-280 flex-col gap-8 px-12 pt-10 pb-24">
        {isLoading ? (
          <>
            <div className="flex flex-col gap-3">
              <Skeleton className="h-3 w-40" />
              <Skeleton className="h-6 w-64" />
            </div>
            <div className="flex gap-8">
              <div className="flex w-52 shrink-0 flex-col gap-3">
                {Array.from({ length: 6 }, (_, i) => (
                  <Skeleton key={i} className="h-3 w-36" />
                ))}
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-6">
                <div className="flex flex-col gap-3 rounded-xl bg-elevated p-4 ring-1 ring-line-2">
                  {Array.from({ length: 6 }, (_, i) => (
                    <Skeleton key={i} className="h-3 w-full" />
                  ))}
                </div>
                <div className="flex flex-col gap-3 rounded-xl bg-elevated p-4 ring-1 ring-line-2">
                  {Array.from({ length: 12 }, (_, i) => (
                    <Skeleton key={i} className="h-3 w-full" />
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <AutoSaveIndicator />
            <div className="flex flex-col gap-3">
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link
                        to="/admin/default-templates"
                        className="inline-flex items-center gap-1"
                      >
                        <ArrowLeft className="size-3.5" />
                        Default Templates
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>Edit</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
              <PageHeader
                title="Edit Default Template"
                description={
                  lastUpdatedAt
                    ? `Last updated ${lastUpdatedAt}${
                        lastUpdatedByEmail ? ` by ${lastUpdatedByEmail}` : ""
                      }`
                    : undefined
                }
                actions={
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={publishedId} className="font-normal">
                        Published
                      </Label>
                      <Switch
                        id={publishedId}
                        checked={status === DefaultTemplateStatus.PUBLISHED}
                        onCheckedChange={handleTogglePublish}
                        disabled={
                          !canTogglePublish ||
                          isLoading ||
                          isSubmitting ||
                          isPublishing ||
                          autoSaveStatus === "saving" ||
                          autoSaveStatus === "error" ||
                          (status !== DefaultTemplateStatus.PUBLISHED &&
                            hasUnsavedChanges)
                        }
                      />
                    </div>
                    <Button
                      form="edit-default-template-form"
                      type="submit"
                      variant="primary"
                      size="sm"
                      disabled={
                        isSubmitting || isLoading || !visa || !draftType
                      }
                    >
                      {isSubmitting ? <Spinner /> : null}
                      {isSubmitting ? "Saving..." : "Save Changes"}
                    </Button>
                    {templateId ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label="More Options"
                            disabled={isLoading || isSubmitting}
                          >
                            <MoreVertical />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem
                            onSelect={() => setIsVersionHistoryOpen(true)}
                          >
                            <Clock />
                            Version History
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </div>
                }
              />
            </div>

            <div className="flex items-start gap-8">
              <nav className="sticky top-6 flex w-52 shrink-0 flex-col gap-3">
                <SectionHeader title="Contents" />
                <div className="max-h-[70vh] overflow-y-auto pr-1">
                  {sections.length > 0 ? (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleSidebarDragEnd}
                    >
                      <SortableContext
                        items={sectionIds}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="flex flex-col gap-0.5">
                          {sections.map((section, index) => {
                            const name = section.section_name ?? "";
                            return (
                              <SidebarSortableItem
                                key={name}
                                id={name}
                                title={name || `Section ${index + 1}`}
                                isSelected={
                                  highlightedSection === name &&
                                  expandedSections.includes(name)
                                }
                                onClick={() =>
                                  name && expandSectionFromSidebar(name)
                                }
                              />
                            );
                          })}
                        </div>
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <p className="px-2 text-sm text-fg-3">No sections yet</p>
                  )}
                </div>
              </nav>

              <div className="flex min-w-0 flex-1 flex-col gap-8">
                <section className="flex flex-col gap-3">
                  <SectionHeader title="Details" />
                  <form
                    id="edit-default-template-form"
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-4 rounded-xl bg-elevated p-4 ring-1 ring-line-2"
                  >
                    <div className="flex flex-wrap items-start gap-4">
                      <div className="flex min-w-55 flex-1 flex-col gap-1.5">
                        <Label htmlFor={visaId}>
                          Visa <RequiredMark />
                        </Label>
                        <Select
                          required
                          value={visa || undefined}
                          onValueChange={(value) => {
                            const nextVisa = value as VISAVALUE;
                            setVisa(nextVisa);
                            latestSnapshotRef.current = {
                              ...latestSnapshotRef.current,
                              visa: nextVisa
                            };
                            startAutoSave(persistLatestTemplate);
                          }}
                          disabled={isLoading || isSubmitting}
                        >
                          <SelectTrigger id={visaId} className="w-full">
                            <SelectValue placeholder="Select visa" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.values(VISAVALUE)
                              .filter((v) => v !== VISAVALUE.EMPTY)
                              .map((v) => (
                                <SelectItem key={v} value={v}>
                                  {v}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex min-w-55 flex-1 flex-col gap-1.5">
                        <div className="flex items-center gap-1">
                          <Label htmlFor={visaSubtypeId}>Visa Subtype</Label>
                          <InfoHint
                            label="About visa subtypes"
                            content={
                              'For workflows that support subtypes (for example: "Asylum"), enter values like "Affirmative" or "Defensive" to distinguish draft variants.'
                            }
                          />
                        </div>
                        <Input
                          id={visaSubtypeId}
                          value={visaSubtype}
                          onChange={(e) => {
                            const nextSubtype = e.target.value;
                            setVisaSubtype(nextSubtype);
                            latestSnapshotRef.current = {
                              ...latestSnapshotRef.current,
                              visaSubtype: nextSubtype
                            };
                            startAutoSave(persistLatestTemplate);
                          }}
                          placeholder="e.g., O-1A: research"
                          disabled={isLoading || isSubmitting}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor={draftTypeId}>
                        Draft Type <RequiredMark />
                      </Label>
                      <Select
                        required
                        value={draftType || undefined}
                        onValueChange={(value) => {
                          const nextDraftType = value as DraftType;
                          setDraftType(nextDraftType);
                          latestSnapshotRef.current = {
                            ...latestSnapshotRef.current,
                            draftType: nextDraftType
                          };
                          startAutoSave(persistLatestTemplate);
                        }}
                        disabled={isLoading || isSubmitting}
                      >
                        <SelectTrigger id={draftTypeId} className="w-full">
                          <SelectValue placeholder="Select draft type" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.values(DraftType).map((dt) => (
                            <SelectItem key={dt} value={dt}>
                              {DRAFT_TYPE_LABELS[dt as DraftType]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Status is API-managed; omit from editing UI */}

                    <div className="flex items-center gap-2">
                      <Label htmlFor={standardId}>
                        Make this the standard template?
                      </Label>
                      <InfoHint
                        label="About standard templates"
                        content={
                          'If checked, this default template will appear with a "Standard" badge.'
                        }
                      />
                      <Switch
                        id={standardId}
                        className="ml-2"
                        checked={isStandard}
                        onCheckedChange={(nextIsStandard) => {
                          setIsStandard(nextIsStandard);
                          latestSnapshotRef.current = {
                            ...latestSnapshotRef.current,
                            isStandard: nextIsStandard
                          };
                          startAutoSave(persistLatestTemplate);
                        }}
                        disabled={isLoading || isSubmitting}
                      />
                    </div>
                  </form>
                </section>

                <section className="flex flex-col gap-3">
                  <SectionHeader
                    title="Sections"
                    actions={
                      <Button size="sm" onClick={handleAddSection}>
                        <Plus />
                        Add Section
                      </Button>
                    }
                  />
                  {sections.length === 0 ? (
                    <EmptyState
                      icon={ListOrdered}
                      title="No Sections Yet"
                      description="Use Add Section to create one."
                      className="rounded-xl bg-elevated ring-1 ring-line-2"
                    />
                  ) : (
                    <div className="flex flex-col gap-3">
                      {sections.map((section) => (
                        <SectionContainer
                          key={section.section_name}
                          title={section.section_name || "Untitled section"}
                          isExpanded={
                            section.section_name
                              ? expandedSections.includes(section.section_name)
                              : false
                          }
                          onToggle={() =>
                            section.section_name &&
                            toggleSectionFromHeader(section.section_name)
                          }
                          isTitleEditable
                          onSaveTitle={(value: string) =>
                            section?.section_name &&
                            handleSaveTitle(section.section_name, value)
                          }
                          headerRight={
                            <Button
                              variant="danger"
                              size="icon-xs"
                              aria-label="Delete section"
                              onClick={() => {
                                setSectionToDelete(section);
                                onOpen();
                              }}
                            >
                              <Trash2 />
                            </Button>
                          }
                          ref={
                            section.section_name
                              ? setSectionRef(section.section_name)
                              : undefined
                          }
                        >
                          <ProseTextArea
                            key={section.section_name}
                            text={section.section_content || ""}
                            updateText={(plainText) =>
                              handleUpdateSectionContent(
                                plainText,
                                section.section_name ?? ""
                              )
                            }
                            customHeight="auto"
                            shouldShowSaveBtn={false}
                            enableBracketHighlighting
                          />
                        </SectionContainer>
                      ))}
                    </div>
                  )}
                </section>
              </div>
            </div>

            <AlertDialog
              open={isOpen}
              onOpenChange={(open) => !open && onClose()}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Section</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this section? This action
                    cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel size="sm">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      if (sectionToDelete?.section_name)
                        handleDeleteSection(sectionToDelete.section_name);
                      onClose();
                    }}
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <TemplateVersionHistoryDrawer
              open={isVersionHistoryOpen}
              onOpenChange={(next) => setIsVersionHistoryOpen(next)}
              templateId={templateId ?? null}
              templateTitle="Default template"
              versions={templateVersions}
              isLoading={isLoadingVersions}
              isLoadingMore={isLoadingMoreVersions}
              hasMore={hasMoreVersions}
              onLoadMore={() => {
                fetchTemplateVersions({ append: true }).catch(() => {});
              }}
              error={versioningError}
              restoringVersionId={restoringVersionId}
              onRestoreClick={handleRestoreClick}
            />

            <AlertDialog
              open={isRestoreConfirmOpen}
              onOpenChange={(next) => {
                if (!next) setIsRestoreConfirmOpen(false);
              }}
            >
              {/* Opens from the version history drawer, which sits at the top z-index. */}
              <AlertDialogContent className="z-2147483647">
                <AlertDialogHeader>
                  <AlertDialogTitle>Restore This Version?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Restoring will replace the current template content. You can
                    restore another version later if needed.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                {pendingRestoreVersion?.created_at ? (
                  <p className="text-sm text-fg-3">
                    Selected version created{" "}
                    {new Date(
                      pendingRestoreVersion.created_at
                    ).toLocaleString()}
                    .
                  </p>
                ) : null}
                <AlertDialogFooter>
                  <AlertDialogCancel
                    size="sm"
                    disabled={Boolean(restoringVersionId)}
                  >
                    Cancel
                  </AlertDialogCancel>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={Boolean(restoringVersionId)}
                    onClick={() => {
                      performRestore().catch(() => {});
                    }}
                  >
                    {restoringVersionId ? <Spinner /> : null}
                    Restore
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </div>
    </div>
  );
};
